import React, { useMemo, useState } from "react";
import {
    CheckCircleFilled,
    MailFilled,
    RobotFilled,
    UserOutlined,
    WarningFilled,
} from "@ant-design/icons";
import { Button } from "../../../../design-system";
import {
    AuditEvent,
    Deal,
    DecisionOption,
    EmailTurn,
    Outcome,
} from "../model/types";
import classes from "./NegotiationStream.module.less";
import dealDrawerClasses from "./DealDrawer.module.less";

interface Props {
    deal: Deal;
    onDecide: (option: DecisionOption) => void;
    onSendMessage: (text: string) => void;
}

interface StreamItem {
    event: AuditEvent;
    email?: EmailTurn;
}

// Audit titles that correspond to an outgoing email from the agent. Other
// agent rows (Trigger detected, Classified, Floor breach, Guardrail check,
// Follow-up nudges, etc.) are system actions with no email payload.
const AGENT_EMAIL_TITLE = /^(Outreach sent|Round \d+ sent|Confirmation requested)$/i;

function isEmailBearing(actor: "agent" | "supplier" | "human", title: string): boolean {
    if (actor === "supplier") return true; // suppliers only speak via email
    if (actor === "agent") return AGENT_EMAIL_TITLE.test(title);
    return false;
}

// Walk the audit trail; pop the next thread email when this row is the kind
// of event that carries email content AND the email's side matches the actor.
function mergeStream(deal: Deal): StreamItem[] {
    const emails = deal.thread.filter(
        (t): t is EmailTurn => t.kind === "email"
    );
    let cursor = 0;
    return deal.auditTrail.map((event) => {
        if (isEmailBearing(event.actor, event.event)) {
            const next = emails[cursor];
            if (next && next.side === event.actor) {
                cursor++;
                return { event, email: next };
            }
        }
        return { event };
    });
}

const AUDIT_YEAR = 2026;

const parseWhen = (label: string): { date: string; time: string } => {
    const parts = label.split("·").map((s) => s.trim());
    return { date: parts[0] || label, time: parts[1] || "" };
};

const formatDateHeader = (date: string): string => {
    if (date.toLowerCase() === "today") return "Today";
    return /\d{4}/.test(date) ? date : `${date}, ${AUDIT_YEAR}`;
};

const ACTOR_ICON = {
    agent: <RobotFilled />,
    supplier: <MailFilled />,
    human: <UserOutlined />,
} as const;

export const NegotiationStream: React.FC<Props> = ({
    deal,
    onDecide,
    onSendMessage,
}) => {
    const items = useMemo(() => mergeStream(deal), [deal]);

    // Group by date (using audit whenLabel — the canonical timeline).
    const groups: Array<{ date: string; rows: StreamItem[] }> = [];
    let current: (typeof groups)[number] | null = null;
    for (const item of items) {
        const { date } = parseWhen(item.event.whenLabel);
        if (!current || current.date !== date) {
            current = { date, rows: [] };
            groups.push(current);
        }
        current.rows.push(item);
    }

    return (
        <div className={classes.stream} data-tour-id="dealThread">
            {groups.map((g) => (
                <div key={g.date} className={classes.dayGroup}>
                    <div className={classes.dayHeader}>
                        {formatDateHeader(g.date)}
                    </div>
                    <div className={classes.dayEvents}>
                        {g.rows.map((item) => (
                            <StreamRow key={item.event.id} item={item} />
                        ))}
                    </div>
                </div>
            ))}

            {deal.outcome && <OutcomeCard outcome={deal.outcome} />}

            {deal.escalation && !deal.escalation.resolution ? (
                <ReplyComposer
                    deal={deal}
                    onDecide={onDecide}
                    onSendMessage={onSendMessage}
                />
            ) : (
                <DirectComposer deal={deal} onSendMessage={onSendMessage} />
            )}
        </div>
    );
};

const StreamRow: React.FC<{ item: StreamItem }> = ({ item }) => {
    const { event, email } = item;
    const { time } = parseWhen(event.whenLabel);
    return (
        <div className={classes.row}>
            <div className={`${classes.icon} ${classes[event.actor]}`}>
                {ACTOR_ICON[event.actor]}
            </div>
            <div className={classes.body}>
                <div className={classes.eventHead}>
                    <span className={classes.eventTitle}>{event.event}</span>
                    {time && <span className={classes.eventTime}>{time}</span>}
                </div>
                {event.detail && (
                    <div className={classes.eventDetail}>{event.detail}</div>
                )}
                {email && (
                    <div className={classes.emailWrap}>
                        <EmailCard email={email} />
                    </div>
                )}
            </div>
        </div>
    );
};

const senderInitials = (from: string): string => {
    const name = from.split(/[·•|,]/)[0].trim();
    const words = name.split(/\s+/).filter(Boolean);
    return words
        .slice(0, 2)
        .map((w) => w[0] ?? "")
        .join("")
        .toUpperCase();
};

const EmailCard: React.FC<{ email: EmailTurn }> = ({ email }) => {
    const toLabel = email.side === "agent" ? "supplier" : "Crestwell Partners Procurement";
    return (
        <div
            className={`${dealDrawerClasses.message} ${dealDrawerClasses[email.side]}`}
            style={{ maxWidth: "100%" }}
        >
            <div className={dealDrawerClasses.bubble}>
                <div className={dealDrawerClasses.bubbleHead}>
                    <div className={dealDrawerClasses.avatar}>
                        {senderInitials(email.from)}
                    </div>
                    <div className={dealDrawerClasses.headText}>
                        <div className={dealDrawerClasses.meta}>
                            <span className={dealDrawerClasses.who}>
                                {email.from}
                            </span>
                            <span className={dealDrawerClasses.when}>
                                {email.whenLabel}
                            </span>
                        </div>
                        <div className={dealDrawerClasses.toLine}>
                            <span className={dealDrawerClasses.toLabel}>
                                To:
                            </span>{" "}
                            {toLabel}
                        </div>
                    </div>
                </div>
                <div className={dealDrawerClasses.bubbleBody}>
                    <div className={dealDrawerClasses.subject}>
                        {email.subject}
                    </div>
                    {email.body.map((p, i) => (
                        <p key={i} className={dealDrawerClasses.para}>
                            {p}
                        </p>
                    ))}
                    {email.terms && (
                        <div className={dealDrawerClasses.terms}>
                            {email.terms.map(([k, v], i) => (
                                <div key={i} className={dealDrawerClasses.term}>
                                    <span>{k}</span>
                                    <span className={dealDrawerClasses.termValue}>
                                        {v}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    {email.closing && (
                        <p className={dealDrawerClasses.para}>{email.closing}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

const OutcomeCard: React.FC<{ outcome: Outcome }> = ({ outcome }) => (
    <div className={classes.outcomeCard}>
        <div className={classes.outcomeHead}>
            <CheckCircleFilled className={classes.outcomeIcon} />
            <div className={classes.outcomeTitle}>Negotiation closed</div>
        </div>
        <div className={classes.outcomeSavings}>{outcome.savingsLabel}</div>
        <div className={classes.outcomeSub}>{outcome.savingsSub}</div>
        <div className={classes.outcomeMetrics}>
            {outcome.metrics.map(([k, v]) => (
                <div key={k} className={classes.outcomeMetric}>
                    <div className={classes.outcomeMetricKey}>{k}</div>
                    <div className={classes.outcomeMetricValue}>{v}</div>
                </div>
            ))}
        </div>
    </div>
);

const ReplyComposer: React.FC<Props> = ({ deal, onDecide, onSendMessage }) => {
    const [reply, setReply] = useState("");
    if (!deal.escalation || deal.escalation.resolution) return null;
    const { title, description, decision } = deal.escalation;
    const quickOptions = decision.options.filter((o) => o.kind !== "take");

    const handleSend = () => {
        if (!reply.trim()) return;
        onSendMessage(reply);
        setReply("");
    };

    return (
        <div className={classes.composer}>
            <div className={classes.composerHead}>
                <WarningFilled className={classes.composerIcon} />
                <div>
                    <div className={classes.composerTitle}>{title}</div>
                    <div className={classes.composerDesc}>{description}</div>
                </div>
            </div>

            {quickOptions.length > 0 && (
                <>
                    <div className={classes.composerLabel}>
                        Pick a quick action
                    </div>
                    <div className={classes.composerOptions}>
                        {quickOptions.map((o) => (
                            <button
                                key={o.id}
                                className={classes.composerOption}
                                onClick={() => onDecide(o)}
                            >
                                <div>
                                    <div className={classes.composerOptionTitle}>
                                        {o.title}
                                    </div>
                                    <div className={classes.composerOptionSub}>
                                        {o.subtitle}
                                    </div>
                                </div>
                                {o.recommended && (
                                    <span className={classes.recBadge}>
                                        Suggested
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </>
            )}

            <div className={classes.composerLabel}>
                — or contact directly {deal.supplierName}
            </div>
            <textarea
                className={classes.composerTextarea}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type your response…"
                rows={5}
            />
            <div className={classes.composerActions}>
                <Button
                    type="primary"
                    onClick={handleSend}
                    disabled={!reply.trim()}
                >
                    Send message
                </Button>
            </div>
        </div>
    );
};

const DirectComposer: React.FC<{
    deal: Deal;
    onSendMessage: (text: string) => void;
}> = ({ deal, onSendMessage }) => {
    const [reply, setReply] = useState("");

    const handleSend = () => {
        if (!reply.trim()) return;
        onSendMessage(reply);
        setReply("");
    };

    return (
        <div className={classes.composerQuiet}>
            <div className={classes.composerQuietTitle}>
                Contact {deal.supplierName} directly
            </div>
            <textarea
                className={classes.composerTextarea}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={`Send a message to ${deal.supplierName}…`}
                rows={4}
            />
            <div className={classes.composerActions}>
                <Button
                    type="primary"
                    onClick={handleSend}
                    disabled={!reply.trim()}
                >
                    Send message
                </Button>
            </div>
        </div>
    );
};

